/**
 * 标准的MathJax设置 - 使用本地MathJax库
 * 支持传统的typesetPromise API，提供更好的兼容性
 */

import { MathJaxContext } from 'better-react-mathjax';

declare global {
  interface Window {
    MathJax: any;
  }
}

// 标准的MathJax配置
const mathJaxConfig = {
  // 加载必要的组件
  loader: {
    load: ['input/tex', 'output/svg', '[tex]/mhchem', '[tex]/physics', '[tex]/amsmath', '[tex]/amssymb']
  },
  
  // TeX输入配置
  tex: {
    packages: { '[+]': ['mhchem', 'physics', 'amsmath', 'amssymb'] },
    inlineMath: [['$', '$'], ['\\(', '\\)']],
    displayMath: [['$$', '$$'], ['\\[', '\\]']],
    processEscapes: true,
    processEnvironments: true,
    macros: {
      placeholderbox: '\\class{placeholder-box}{\\Box}',
      // 化学反应箭头宏定义
      reaction: ['\\ce{#1 -> #2}', 2],
      equilibrium: ['\\ce{#1 <=> #2}', 2],
      heating: ['\\xrightarrow{\\text{加热}}'],
      catalyst: ['\\xrightarrow{\\text{催化剂}}'],
      ignite: ['\\xrightarrow{\\text{点燃}}']
    }
  },
  
  // SVG输出配置
  svg: {
    fontCache: 'global',
    displayAlign: 'left',
    displayIndent: '0em',
    scale: 1,
    minScale: 0.5,
    mtextInheritFont: false,
    merrorInheritFont: true,
    mathmlSpacing: false,
    skipAttributes: {},
    exFactor: 0.5,
    displayOverflow: 'linebreak',
    linebreaks: { automatic: false }
  },
  
  // 启动配置
  startup: {
    // 启用DOM渲染
    typeset: true,
    
    ready() {
      const MathJax = window.MathJax;
      
      // 调用默认初始化
      MathJax.startup.defaultReady();
      
      // 确保typesetPromise方法可用
      if (MathJax.typesetPromise) {
        console.log('✅ MathJax DOM渲染模式初始化完成 - typesetPromise可用');
      } else {
        console.warn('❌ MathJax typesetPromise方法不可用');
      }
    }
  },
  
  // 选项配置
  options: {
    enableMenu: false,
    skipHtmlTags: ['script', 'noscript', 'style', 'textarea', 'pre', 'code', 'a'],
    ignoreHtmlClass: 'tex2jax_ignore',
    processHtmlClass: 'tex2jax_process'
  }
};

// 初始化MathJax - 使用本地库
export const initMathJax = () => {
  if (typeof window === 'undefined') {
    return;
  }

  // 设置配置 - better-react-mathjax会处理加载
  window.MathJax = mathJaxConfig;
  console.log('✅ 使用本地MathJax库初始化完成');
};

// 提供一个安全的DOM渲染方法
export const safeTypesetPromise = async (elements: Element[]) => {
  try {
    if (window.MathJax && typeof window.MathJax.typesetPromise === 'function') {
      await window.MathJax.typesetPromise(elements);
    } else {
      console.warn('MathJax typesetPromise不可用');
    }
  } catch (error) {
    console.warn('MathJax渲染错误:', error);
    throw error;
  }
};

// 导出配置供React组件使用
export { mathJaxConfig };

// 自动初始化
if (typeof window !== 'undefined') {
  initMathJax();
}